import React, { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import api from "../lib/api";
import { Clock, Utensils, AlertCircle } from "lucide-react";

export default function Fasting() {
  const [profile, setProfile] = useState(null);
  useEffect(() => { api.get("/profile").then((r) => setProfile(r.data)).catch(() => {}); }, []);

  const isWoman = profile?.sex === "mujer";

  const calorieRange = (() => {
    if (!profile) return null;
    if (profile.sex === "mujer") return "1200 - 1600 kcal";
    return "1700 - 2200 kcal";
  })();

  return (
    <AppShell title="Ayuno 18h + déficit calórico" subtitle="Una estructura clara para adelgazar de forma sostenida.">
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 mep-card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-[rgba(232,61,132,0.12)] border border-[var(--mep-pink)]/40 flex items-center justify-center text-[var(--mep-pink)]">
              <Clock size={18} />
            </div>
            <h2 className="font-display text-2xl font-light tracking-tight">Cómo funciona el ayuno 18:6</h2>
          </div>
          <ul className="space-y-3 text-sm leading-relaxed">
            <Li>Ayuno de <b>18 horas</b> diarias (solo agua, café o té sin azúcar).</Li>
            <Li>Ventana de comida de <b>6 horas</b> (ejemplo: 12:00 a 18:00).</Li>
            <Li><b>Desayuno abundante en proteína</b> al romper el ayuno (huevos, yogur griego, batido proteico).</Li>
            <Li><b>Snack</b> a media tarde para sostener energía (yogur griego, fruta o un batido ligero).</Li>
            <Li><b>Cena temprana a las 18:00</b> con proteína suficiente y vegetales.</Li>
            <Li>Mantén un <b>déficit calórico moderado</b> según tu peso y estatura.</Li>
          </ul>
        </div>

        <div className="mep-card p-6">
          <div className="text-eyebrow mb-3">Tu rango calórico <span className="normal-case tracking-normal text-[var(--mep-text-muted)] text-[10px]">(Consumo diario recomendado para no pasar hambre)</span></div>
          {profile ? (
            <>
              <div className="font-display text-3xl text-[var(--mep-gold)] font-light tracking-tight">
                {calorieRange}
              </div>
              <p className="text-xs text-[var(--mep-text-muted)] mt-2 uppercase tracking-wider">
                Aprox. para tu {profile.sex === "mujer" ? "perfil femenino" : "perfil masculino"}
              </p>
            </>
          ) : (
            <p className="text-sm text-[var(--mep-text-muted)]">Crea tu perfil para personalizar.</p>
          )}
        </div>

        <div className="mep-card p-6 lg:col-span-3">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-full bg-[rgba(212,175,55,0.12)] border border-[var(--mep-gold)]/40 flex items-center justify-center text-[var(--mep-gold)]">
              <Utensils size={18} />
            </div>
            <h2 className="font-display text-2xl font-light tracking-tight">Ejemplo de día saludable en ayuno (déficit calórico)</h2>
          </div>
          <div className="mb-5 p-4 rounded-lg bg-[rgba(232,61,132,0.08)] border-l-2 border-[var(--mep-pink)] text-sm text-[var(--mep-text-muted)] leading-relaxed">
            Este es solo un ejemplo orientativo de horario y estructura de comidas. Tus recetas pueden variar dentro de las opciones disponibles en la app.
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <PlanBlock
              title="Perfil femenino (déficit para pérdida de grasa)"
              subtitle="Aprox. 1200–1600 kcal/día"
              items={[
                "12:00 pm — Desayuno\n3 huevos hervidos + 2 lascas de queso gouda + 1 plátano maduro",
                "3:00 pm — Snack\n1 yogurt griego light + 3 fresas",
                "6:00 pm — Cena\n1 bistec de pechuga de pollo grande + ensalada",
              ]}
              footer="Calorías orientadas a pérdida de grasa según perfil, peso y objetivo."
            />
            <PlanBlock
              title="Perfil masculino (déficit para pérdida de grasa)"
              subtitle="Aprox. 1700–2200 kcal/día"
              items={[
                "12:00 pm — Desayuno\n4 huevos hervidos + 1/2 plátano + avena",
                "3:00 pm — Snack\n1 yogurt griego light + 5 arándanos",
                "6:00 pm — Cena\n1 bistec de pescado grande + ensalada",
              ]}
              footer="Las calorías pueden variar según peso, altura, edad y objetivo fitness."
            />
          </div>
        </div>

        <div className="lg:col-span-3 p-5 rounded-xl border border-[var(--mep-gold)]/30 bg-[rgba(212,175,55,0.05)]">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-[var(--mep-gold)] flex-shrink-0 mt-0.5" size={18} />
            <p className="text-sm">
              <b>Aviso:</b> el ayuno intermitente no es recomendable durante embarazo, lactancia, trastornos alimenticios o condiciones médicas específicas. Consulta a tu médico antes de comenzar.
            </p>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

const Li = ({ children }) => (
  <li className="flex gap-3"><span className="text-[var(--mep-gold)] mt-1">•</span><span>{children}</span></li>
);

const PlanBlock = ({ title, subtitle, items, footer }) => (
  <div className="bg-[var(--mep-surface-2)] rounded-lg p-5 border-l-2 border-[var(--mep-pink)]">
    <h3 className="font-display text-lg font-medium tracking-tight">{title}</h3>
    {subtitle && (
      <p className="text-xs text-[var(--mep-gold)] uppercase tracking-[0.15em] mt-1 mb-4">{subtitle}</p>
    )}
    <ul className="space-y-3 text-sm">
      {items.map((it, i) => {
        const lines = it.split("\n");
        return (
          <li key={i} className="text-[var(--mep-text-muted)] leading-relaxed">
            <span className="text-[var(--mep-text)] font-medium">{lines[0]}</span>
            {lines[1] && <span className="block">{lines[1]}</span>}
          </li>
        );
      })}
    </ul>
    {footer && (
      <p className="mt-4 pt-3 border-t border-[var(--mep-border)] text-xs text-[var(--mep-gold)] leading-relaxed">
        {footer}
      </p>
    )}
  </div>
);
